/**
 * Where a visitor came from, carried to the enquiry they send later.
 *
 * TWO TOUCHES. The latest campaign touch -- the one an Ads conversion is
 * credited to, and a new campaign replaces the old one whole -- and the first
 * touch, which is written once and never overwritten by a later visit, direct
 * or otherwise. Together they answer both "which ad closed this lead" and
 * "which channel found them".
 *
 * TWO LIFETIMES, and consent decides which. By default everything lives in
 * sessionStorage and is gone when the tab closes, as it always has: nothing
 * leaves the browser unless the visitor submits a form, and no cookie is set.
 * Only if the visitor has accepted marketing cookies is a copy kept in
 * localStorage for 90 days -- Google's window for importing a click as an
 * offline conversion -- so a visitor who clicks an ad, closes the tab and comes
 * back directly next week still brings the gclid with their enquiry. Refusing
 * or withdrawing marketing consent deletes that copy.
 *
 * Every read and write is guarded: private browsing and full quotas throw, and a
 * broken attribution store must never break the page or the form.
 */
import { readStoredConsent } from './analytics';

export const CAMPAIGN_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
  'gclid',
  'gbraid',
  'wbraid',
] as const;

/** The first touch, flattened with a `first_` prefix so the server can store it beside the latest. */
export const FIRST_TOUCH_KEYS = [
  'first_utm_source',
  'first_utm_medium',
  'first_utm_campaign',
  'first_utm_term',
  'first_utm_content',
  'first_gclid',
  'first_landing_page',
  'first_referrer',
  'first_touch_at',
] as const;

/**
 * Everything a submission may carry. The server strips anything else, so a key
 * added here must be added to `employerSubmission` in
 * supabase/functions/_shared/schemas/submissions.ts as well --
 * attribution.test.ts reads that file and fails if the two drift.
 */
export const ATTRIBUTION_KEYS = [
  ...CAMPAIGN_KEYS,
  'landing_page',
  'referrer',
  'page_url',
  'last_touch_at',
  ...FIRST_TOUCH_KEYS,
] as const;

export type AttributionKey = (typeof ATTRIBUTION_KEYS)[number];
export type Attribution = Partial<Record<AttributionKey, string>>;

const SESSION_KEY = 'waq-attribution';
const FIRST_KEY = 'waq-attribution-first';
const PERSIST_KEY = 'waq-attribution-persist';

/** Google Ads accepts an offline conversion up to 90 days after the click. */
const PERSIST_MS = 90 * 24 * 60 * 60 * 1000;

/** The server rejects anything longer, so a pasted monster URL cannot cost a lead. */
const MAX_LENGTH = 500;
const clip = (value: string) => value.slice(0, MAX_LENGTH);

export type AttributionStores = {
  /** Defaults to sessionStorage. */
  session?: Storage;
  /** Defaults to localStorage. Used only when `persist` is true. */
  persistent?: Storage;
  /** Defaults to "the visitor accepted marketing cookies". */
  persist?: boolean;
  now?: Date;
};

type Persisted = { first?: Attribution; last?: Attribution };

/** Accepts a bare Storage too, as the first version of this module did. */
function resolve(stores: Storage | AttributionStores = {}) {
  const options: AttributionStores = 'getItem' in stores ? { session: stores as Storage } : stores;
  const persist = options.persist ?? Boolean(readStoredConsent()?.marketing);
  return {
    session: options.session ?? sessionStorage,
    persistent: persist ? (options.persistent ?? localStorage) : undefined,
    now: options.now ?? new Date(),
  };
}

function load<T extends object>(storage: Storage | undefined, key: string): T {
  if (!storage) return {} as T;
  try {
    return JSON.parse(storage.getItem(key) || '{}') as T;
  } catch {
    return {} as T;
  }
}

function save(storage: Storage | undefined, key: string, value: object): void {
  if (!storage) return;
  try {
    storage.setItem(key, JSON.stringify(value));
  } catch {
    // Private mode or a full quota: go without rather than throw.
  }
}

/** True when the touch is older than the 90 days anything is kept for. */
function expired(at: string | undefined, now: Date): boolean {
  if (!at) return true;
  const time = Date.parse(at);
  return Number.isNaN(time) || now.getTime() - time > PERSIST_MS;
}

function externalReferrer(referrer: string, host: string): string | undefined {
  if (!referrer) return undefined;
  try {
    return new URL(referrer).host !== host ? clip(referrer) : undefined;
  } catch {
    return undefined;
  }
}

/** Only the parts of the latest touch that should outlive the tab. */
function latestCampaign(value: Attribution): Attribution {
  const out: Attribution = {};
  for (const key of [...CAMPAIGN_KEYS, 'landing_page', 'last_touch_at'] as const) {
    if (value[key]) out[key] = value[key];
  }
  return out;
}

const hasCampaign = (value: Attribution) => CAMPAIGN_KEYS.some((key) => value[key]);

/** Called on every route change with the full URL and `document.referrer`. */
export function captureAttribution(
  url: string,
  referrer: string,
  stores: Storage | AttributionStores = {}
): void {
  const { session, persistent, now } = resolve(stores);

  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return;
  }

  // --- Latest touch, in the session store (unchanged behaviour). ---
  const current = load<Attribution>(session, SESSION_KEY);
  const next: Attribution = { ...current };

  const campaign = CAMPAIGN_KEYS.filter((key) => parsed.searchParams.get(key));
  if (campaign.length > 0) {
    // Last click wins, as it does in Ads: a new campaign replaces the old set whole.
    for (const key of CAMPAIGN_KEYS) delete next[key];
    for (const key of campaign) next[key] = clip(parsed.searchParams.get(key) ?? '');
    next.landing_page = clip(url);
    next.last_touch_at = now.toISOString();
  } else if (!next.landing_page) {
    next.landing_page = clip(url);
  }

  // The first outside referrer of the session; the site linking to itself is not one.
  const outside = externalReferrer(referrer, parsed.host);
  if (!current.referrer && outside) next.referrer = outside;

  save(session, SESSION_KEY, next);

  // --- First touch: written once, never replaced. ---
  const persisted = load<Persisted>(persistent, PERSIST_KEY);
  const persistedFirst =
    persisted.first && !expired(persisted.first.first_touch_at, now) ? persisted.first : undefined;
  let first = persistedFirst ?? load<Attribution>(session, FIRST_KEY);

  if (!first.first_touch_at) {
    first = { first_landing_page: clip(url), first_touch_at: now.toISOString() };
    for (const key of [
      'utm_source',
      'utm_medium',
      'utm_campaign',
      'utm_term',
      'utm_content',
      'gclid',
    ] as const) {
      const value = parsed.searchParams.get(key);
      if (value) first[`first_${key}`] = clip(value);
    }
    if (outside) first.first_referrer = outside;
  }
  save(session, FIRST_KEY, first);

  // --- The 90-day copy, only with marketing consent. ---
  if (persistent) {
    const persistedLast =
      persisted.last && !expired(persisted.last.last_touch_at, now) ? persisted.last : undefined;
    const last = hasCampaign(next) ? latestCampaign(next) : persistedLast;
    save(persistent, PERSIST_KEY, { first, ...(last ? { last } : {}) });
  } else {
    forgetPersistentAttribution();
  }
}

/**
 * What travels with a submission: the latest touch, the first touch and the
 * page it was sent from. With marketing consent, a campaign from an earlier
 * visit fills in when this tab arrived without one.
 */
export function readAttribution(
  pageUrl: string,
  stores: Storage | AttributionStores = {}
): Attribution {
  const { session, persistent, now } = resolve(stores);

  let latest = load<Attribution>(session, SESSION_KEY);
  const persisted = load<Persisted>(persistent, PERSIST_KEY);

  if (!hasCampaign(latest) && persisted.last && !expired(persisted.last.last_touch_at, now)) {
    latest = { ...latest, ...latestCampaign(persisted.last) };
  }

  const first =
    persisted.first && !expired(persisted.first.first_touch_at, now)
      ? persisted.first
      : load<Attribution>(session, FIRST_KEY);

  return { ...latest, ...first, page_url: clip(pageUrl) };
}

/** Called when marketing consent is refused or withdrawn. */
export function forgetPersistentAttribution(storage?: Storage): void {
  try {
    (storage ?? localStorage).removeItem(PERSIST_KEY);
  } catch {
    // Nothing stored, or storage unavailable: nothing to forget.
  }
}
